import { pickedFile } from '../../utils/pickedFile.js'
import MediaCaptureField from '../common/MediaCaptureField.js'
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SelectField from '../common/SelectField.js';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from "../common/Loader.js"
import Alert from '../common/Alert.js';
import expenseService from '../../services/ExpenseService.js';
import { locationHubService } from '../../services/locationHubService.js';
import { formatHubBranchLabel, filterShipmentLocations } from '../../utils/destinationType.js';
import { IndianRupee } from "lucide-react";
import { validators, inputFilters, useValidation, ValidationMessage } from '../common/Validation.js';
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';

const EditExpense = () => {
  const { expenseId } = useParams();
  const [formData, setFormData] = useState({
    category: '',
    month: '',
    year: '',
    expense_date: '',
    amount: '',
    description: '',
    vendor_name: '',
    bill_number: '',
    remarks: '',
    status: '',
    payment_mode: '',
    transaction_id: '',
    paid_to: '',
    location_hub_id: '',
  });
  
  const [fileAttachment, setFileAttachment] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [existingFile, setExistingFile] = useState(null);
  const [hasExistingFile, setHasExistingFile] = useState(false);
  const [categories, setCategories] = useState([]);
  const [months, setMonths] = useState([]);
  const [locationHubs, setLocationHubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const { errors, validate, validateField } = useValidation();

  const validationRules = {
    category:     (v) => validators.select(v, 'category'),
    expense_date: (v) => validators.required(v, 'Expense date'),
    amount:       (v) => validators.required(v, 'Amount') || validators.number(v, { min: 0.01, label: 'Amount' }),
    description:  (v) => validators.required(v, 'Description'),
    location_hub_id: (v) => validators.select(v, 'location hub'),
  };

  const selectedHub = locationHubs.find(
    (h) => String(h.id) === String(formData.location_hub_id)
  );

  const paymentModes = ['cash', 'upi'];
  const paymentModeOptions = (
    formData.payment_mode && !paymentModes.includes(formData.payment_mode)
      ? [formData.payment_mode, ...paymentModes]
      : paymentModes
  );
  const statuses = ['pending', 'approved', 'paid', 'rejected', 'cancelled'];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);

  useEffect(() => {
    fetchExpenseAndDropdowns();
  }, [expenseId]);

  const fetchExpenseAndDropdowns = async () => {
    try {
      const [expenseRes, categoriesRes, monthsRes, hubsRes] = await Promise.all([
        expenseService.getById(expenseId),
        expenseService.getCategories(),
        expenseService.getMonths(),
        locationHubService.getHubsForSelect(),
      ]);
      
      const expenseData = expenseRes.data;
      setFormData({
        category: expenseData.category || '',
        month: expenseData.month || '',
        year: expenseData.year?.toString() || '',
        expense_date: expenseData.expense_date || '',
        amount: expenseData.amount || '',
        description: expenseData.description || '',
        vendor_name: expenseData.vendor_name || '',
        bill_number: expenseData.bill_number || '',
        remarks: expenseData.remarks || '',
        status: expenseData.status || '',
        payment_mode: expenseData.payment_mode || '',
        transaction_id: expenseData.transaction_id || '',
        paid_to: expenseData.paid_to || '',
        location_hub_id: expenseData.location_hub_id != null ? String(expenseData.location_hub_id) : '',
      });

      setCategories(categoriesRes.data || []);
      setMonths(monthsRes.data || []);
      setLocationHubs(filterShipmentLocations(hubsRes.data?.hubs || []));
      
      // Check if file attachment exists
      if (expenseData.has_attachment) {
        setHasExistingFile(true);
        setExistingFile(expenseData.attachment_url);
      }
      
    } catch (err) {
      Alert.error('Failed to load expense data');
      console.error(err);
      navigate('/expenses');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    let filtered = value;
    if (name === 'amount') filtered = inputFilters.amount(value);

    setFormData((prev) => ({ ...prev, [name]: filtered }));

    if (validationRules[name]) {
      validateField(name, filtered, validationRules[name]);
    }
  };

  const handleFileChange = (e) => {
    const file = pickedFile(e);
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Alert.error('File must be less than 5MB');
        return;
      }
      setFileAttachment(file);
      const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
      if (isPdf) {
        setFilePreview('pdf');
      } else {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFilePreview(reader.result);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const removeFile = () => {
    setFileAttachment(null);
    setFilePreview(null);
    const fileInput = document.getElementById('file-attachment-input');
    if (fileInput) fileInput.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate(formData, validationRules)) {
      Alert.error('Please fix the highlighted errors before submitting');
      return;
    }
    if (!hasExistingFile && !fileAttachment) {
      Alert.error('File attachment is required');
      return;
    }

    setSubmitting(true);

    try {
      await expenseService.update(expenseId, formData, fileAttachment);
      Alert.success('Expense updated successfully!');
      setTimeout(() => navigate('/expenses'), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update expense');
    } finally {
      setSubmitting(false);
    }
  };

  const openFileInNewTab = () => {
    if (existingFile) {
      window.open(existingFile, '_blank');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading expense data..." />
        </div>
      </DashboardLayout>
    );
  }

  const isPaidStatus = formData.status === 'paid';

  return (
    <DashboardLayout>
              <div className="w-full min-w-0 max-w-full mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-4">
                  <div>
                    <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                      <IndianRupee className="w-5 h-5 text-buttonBg" /> 
                      Edit Expense
                    </h1>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm p-4 sm:p-8 space-y-6 sm:space-y-8">
                  
                  {/* Section 1: Basic Expense Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Basic Information
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-2 xl:grid-cols-4 gap-2 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Category <span className="text-danger font-bold">*</span>
                        </label>
                        <SelectField variant="form"
                          name="category"
                          value={
                            categories.some((c) => c.value === formData.category)
                              ? formData.category
                              : 'other'
                          }
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v === 'other') {
                              setFormData((prev) => ({
                                ...prev,
                                category: prev.custom_category || 'other',
                                custom_category: prev.custom_category || (categories.some((c) => c.value === prev.category) ? '' : prev.category),
                              }));
                            } else {
                              setFormData((prev) => ({ ...prev, category: v, custom_category: '' }));
                            }
                          }}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.category ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        >
                          {categories.filter((c) => c.value !== 'other').map((cat) => (
                            <option key={cat.value} value={cat.value}>
                              {cat.label}
                            </option>
                          ))}
                          <option value="other">Other</option>
                        </SelectField>
                        {(formData.custom_category || !categories.some((c) => c.value === formData.category)) && (
                          <input
                            type="text"
                            value={formData.custom_category || (categories.some((c) => c.value === formData.category) ? '' : formData.category)}
                            onChange={(e) => setFormData((prev) => ({
                              ...prev,
                              custom_category: e.target.value,
                              category: e.target.value || 'other',
                            }))}
                            placeholder="Enter category"
                            className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-xl text-sm"
                          />
                        )}
                        <ValidationMessage error={errors.category} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Expense Date <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="date"
                          name="expense_date"
                          value={formData.expense_date}
                          onChange={handleChange}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.expense_date ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.expense_date} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Amount (₹) <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="text"
                          name="amount"
                          value={formData.amount}
                          onChange={handleChange}
                          placeholder="Enter amount"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.amount ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.amount} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Location Hub Name <span className="text-danger font-bold">*</span>
                        </label>
                        <SelectField variant="form"
                          name="location_hub_id"
                          value={formData.location_hub_id}
                          onChange={handleChange}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.location_hub_id ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        >
                          <option value="">Select location hub</option>
                          {locationHubs.map((hub) => (
                            <option key={hub.id} value={hub.id}>
                              {formatHubBranchLabel(hub) || hub.location}
                            </option>
                          ))}
                        </SelectField>
                        <ValidationMessage error={errors.location_hub_id} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Location Hub ID
                        </label>
                        <input
                          type="text"
                          value={selectedHub?.hub_id || ''}
                          readOnly
                          placeholder="Auto-filled from hub"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl bg-gray-50 text-gray-700 focus:outline-none text-sm sm:text-base"
                        />
                      </div>

                      <div className="sm:col-span-3">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Description <span className="text-danger font-bold">*</span>
                        </label>
                        <textarea
                          name="description"
                          value={formData.description}
                          onChange={handleChange}
                          placeholder="Enter expense description"
                          rows="1"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.description ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.description} />
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-2 gap-2 sm:gap-6">
                    {/* Section 2: Vendor Information */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Vendor Information
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-2 xl:grid-cols-4 gap-2 sm:gap-6">
                        <div className="md:col-span-2">
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Vendor Name
                          </label>
                          <input
                            type="text"
                            name="vendor_name"
                            value={formData.vendor_name}
                            onChange={handleChange}
                            placeholder="Enter vendor name"
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          />
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Bill Number
                          </label>
                          <input
                            type="text"
                            name="bill_number"
                            value={formData.bill_number}
                            onChange={handleChange}
                            placeholder="Enter bill number"
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Section 4: File Attachment & Remarks */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Additional Information
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-2 gap-2 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            File Attachment {!hasExistingFile && <span className="text-danger font-bold">*</span>}
                          </label>
                          
                          {hasExistingFile && !filePreview && (
                            <button
                              type="button"
                              onClick={openFileInNewTab}
                              className="mb-3 px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors text-sm font-medium flex items-center gap-2"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              View Existing File
                            </button>
                          )}

                          <MediaCaptureField mode="document" onPick={handleFileChange} />
                          <p className="text-xs text-gray-600 mt-1">
                            {hasExistingFile ? 'Upload new file to replace existing one (Max 5MB)' : 'Accepted formats: JPG, PNG, GIF, WebP, PDF (Max 5MB)'}
                          </p>
                          
                          {filePreview && (
                            <div className="mt-3">
                              <p className="text-sm font-medium text-gray-700 mb-2">New File Preview:</p>
                              <div className="relative inline-block">
                                {filePreview === 'pdf' ? (
                                  <div className="w-32 h-32 sm:w-40 sm:h-40 flex flex-col items-center justify-center border-2 border-gray-300 rounded-md bg-gray-50">
                                    <span className="text-3xl">📄</span>
                                    <span className="text-xs text-gray-500 mt-1">PDF File</span>
                                    <span className="text-xs text-gray-400 truncate max-w-[120px] px-1">{fileAttachment?.name}</span>
                                  </div>
                                ) : (
                                  <img 
                                    src={filePreview} 
                                    alt="File Preview" 
                                    className="w-32 h-32 sm:w-40 sm:h-40 object-cover border-2 border-gray-300 rounded-md"
                                  />
                                )}
                                <button
                                  type="button"
                                  onClick={removeFile}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors text-sm"
                                  title="Remove file"
                                >
                                  ×
                                </button>
                              </div>
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Remarks
                          </label>
                          <textarea
                            name="remarks"
                            value={formData.remarks}
                            onChange={handleChange}
                            placeholder="Enter any additional remarks"
                            rows="1"
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Status & Payment */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Status & Payment Details
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-2 xl:grid-cols-4 gap-2 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Status
                        </label>
                        <SelectField variant="form"
                          name="status"
                          value={formData.status}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        >
                          {statuses.map((status) => (
                            <option key={status} value={status}>
                              {status.charAt(0).toUpperCase() + status.slice(1)}
                            </option>
                          ))}
                        </SelectField>
                      </div>

                      {isPaidStatus && (
                        <>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                              Payment Mode
                            </label>
                            <SelectField variant="form"
                              name="payment_mode"
                              value={formData.payment_mode}
                              onChange={handleChange}
                              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                            >
                              {paymentModeOptions.map((mode) => (
                                <option key={mode} value={mode}>
                                  {mode.replace(/_/g, ' ').toUpperCase()}
                                </option>
                              ))}
                            </SelectField>
                          </div>

                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                              Transaction ID
                            </label>
                            <input
                              type="text"
                              name="transaction_id"
                              value={formData.transaction_id}
                              onChange={handleChange}
                              placeholder="Enter transaction ID"
                              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                            />
                          </div>

                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                              Paid To
                            </label>
                            <input
                              type="text"
                              name="paid_to"
                              value={formData.paid_to}
                              onChange={handleChange}
                              placeholder="Enter payee name"
                              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4 sm:pt-6 border-t">
                    <Button
                      variant="primary"
                      type="submit"
                      loading={submitting}
                    >
                      {submitting ? 'Updating...' : 'Update Expense'}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => navigate('/expenses')}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>
    </DashboardLayout>
  );
};

export default EditExpense;